/* eslint-disable max-lines-per-function */
import $ from 'zepto';
import { getXoom } from '../xoom';
import initFilters from '../form/Filters';
import initInlineErrors from '../form/InlineErrors';
import initUIManager from './UIManager';

// Use this to get accurate Send Receive amount quotes from the server
const initQuote = () => {
  const xoom = getXoom();
  const ajaxCache = {};
  let xhr;
  let ajaxCallback;
  const ajaxTimeoutExpirationMs = 15000;
  let quoteProcessingState = false;
  let startValidationCallback;
  let updateCurrencyCallback;
  let pendingAjaxCallback;
  let roundingMessageTimeout;
  let result = {};
  let sendAmountElementSelector;
  let receiveAmountElementSelector;
  let sendAmountElementId;
  let receiveAmountElementId;
  let sendAmountErrorManagerInstance;
  let receiveAmountMessageManagerInstance;
  let opts;
  let isValidAmount = 0;
  let validateTimeout;
  const EMPTY_STRING = '';
  let grayOutField;
  let removeSpinnerTimeout;
  let sendLimitsURL;
  let paramsForQuote;
  let messages;
  let messageContainer;
  let amountFieldParentObj;
  let inlineErrorCustomClasses;
  let msgClass;
  let $sendAmount;
  let $receiveAmount;
  let $receiveAmountEntered;
  let $currencySelect;
  let $sourceCurrencySelect;
  let $orderItemId;
  const loading = "<p class='ppvx_loading-spinner spinner' role='alert'></p>";
  let loadingSpinnerActive = false;
  const amountsCache = {
    sendAmount: '',
    receiveAmount: '',
  };
  const defaultOptions = {
    initialCountry: EMPTY_STRING,
    destCurrencyCode: 'USD',
    sourceCurrencyCode: 'USD',
    receiveAmtEntered: false,
    isValidAmountCallback: null,
    isInvalidAmountCallback: null,
    emptyValueCallback: null,
    reloadOnAjaxError: true,
    limits: null, /* optional object specifying upper and lower sending limits */
  };
  const defaultFieldSelectors = {
    exchangeRateSelector: '.exchange-rate-row',
  };
  const state = {
    countryCode: null,
    currency: null,
    sendAmount: null,
    receiveAmount: null,
    sourceCurrencyCode: 'USD',
    quoteURL: null,
    localizedContext: null,
    receiveAmountEntered: false,
    sraType: null,
    sraAmount: null,
  };

  if (!xoom.UIManager) {
    initUIManager();
  }

  if (!xoom.form.Filters) {
    initFilters();
  }

  if (!xoom.form.InlineErrors) {
    initInlineErrors();
  }

  /**
   * message information
   * @typedef {Object} MessageObject
   * @property {string} message Message to be displayed
   * @property {string} messageLevel - Message level [info/warn/error]
   * @property {object} analytics - Analytics information
   * @property {string} analytics.type - The type of error
   * @property {string} analytics.message - Plain text message to be sent to analytics
   */

  function setSraType(receiveAmtEntered) {
    if (receiveAmtEntered) {
      state.sraType = 'targetAmount';
    } else {
      state.sraType = 'sourceAmount';
    }
  }

  /**
   Set whether user entered a Send Amount or Receive Amount so
    that we can pass that information to the back end for proper exchange rate conversions
    @method setReceiveAmtEntered
    @param {Boolean} receiveAmtEntered This is true if the user entered a receive amount
    @public
    */
  function setReceiveAmtEntered(receiveAmtEntered) {
    state.receiveAmountEntered = receiveAmtEntered;
    setSraType(receiveAmtEntered);
  }

  /**
   * Send message event to Segment analytics
   * @method recordMessagePresentedEvents
   * @param {Object} analytics
   * @param {Object} analytics.type Message code/key
   * @param {Object} analytics.message Message that was displayed
   * @private
   */
  function recordMessagePresentedEvents(analytics) {
    if (analytics && xoom && xoom.util && xoom.util.Analytics && xoom.util.Analytics.segment) {
      xoom.util.Analytics.segment.track('Message Presented', {
        type: analytics.type,
        message: analytics.message,
      });
    }
  }

  /**
   Remove spinner from input field when data is successfully returned
    @method removeSpinner
    @private
    */
  function removeSpinner() {
    if (grayOutField) {
      clearTimeout(grayOutField);
    }

    if (removeSpinnerTimeout) {
      clearTimeout(removeSpinnerTimeout);
    }

    $('.spinner').remove();
    $('.loading').removeClass('loading');
    loadingSpinnerActive = false;
  }

  /**
   Show spinner in the Send Amount or Receive Amount field
    while data is still being retrieved/updated
    @method showSpinnerInField
    @param {Object} $obj jQuery object after which the spinner will be placed
    @private
    */
  function showSpinnerInField($obj) {
    clearTimeout(removeSpinnerTimeout);

    grayOutField = setTimeout(() => {
      if (!loadingSpinnerActive) {
        if (amountFieldParentObj && $obj.closest(amountFieldParentObj)) {
          $obj.closest(amountFieldParentObj).addClass('loading');
        } else {
          $obj.parent().addClass('loading');
        }

        $obj.after(loading);
        loadingSpinnerActive = true;
      }
    }, 500);

    removeSpinnerTimeout = setTimeout(removeSpinner, ajaxTimeoutExpirationMs);
  }

  /**
   * Get the state of the processing of Quote information
   * @returns {boolean}
   */
  function getQuoteProcessingState() {
    return quoteProcessingState;
  }

  /**
   * Set the state of the processing of Quote information
   * @param {boolean} isQuoteProcessing the state of Quote processing
   */
  function setQuoteProcessingState(isQuoteProcessing) {
    quoteProcessingState = isQuoteProcessing;
  }

  /**
   * This is to cache the last entered send or receive amounts
   */
  function setAmountsCache() {
    // Set these values so that we can use them to determine whether to proceed with ajax call
    // on the keyup events of sendAmount and receiveAmount
    amountsCache.sendAmount = $sendAmount.val();
    amountsCache.receiveAmount = $receiveAmount.val();
  }

  /**
   * Check for valid numbers entered
   * @param {String} numberString number entered
   * @returns {*}
   */
  function getNumber(numberString) {
    const numberStringReplace = numberString.replace(/[$,]/g, EMPTY_STRING);
    const number = parseFloat(numberStringReplace);
    if (!Number.isNaN(number) && Number.isFinite(number)) {
      return number;
    }
    return false;
  }

  /**
   * @method showSendLimitError
   * @param {boolean} hasError
   * @param {MessageObject} sendLimitError
   * @private
   */
  function showSendLimitError(hasError, sendLimitError) {
    isValidAmount = hasError ? 0 : 1;
    receiveAmountMessageManagerInstance.hide();
    if (state.receiveAmountEntered) {
      receiveAmountMessageManagerInstance
        .show(sendLimitError.message, sendLimitError.messageLevel, sendLimitError.analytics);
    } else {
      sendAmountErrorManagerInstance
        .show(sendLimitError.message, sendLimitError.messageLevel, null, sendLimitError.analytics);
    }
  }

  /**
   * @method showAmountRoundedMessage
   * @param {MessageObject} amountRounded
   * @private
   */
  function showAmountRoundedMessage(amountRounded) {
    if (amountRounded && amountRounded.message && state.currency !== state.sourceCurrencyCode) {
      isValidAmount = 0;

      /* We need to give time for the form fields to be cleared on form submit before
            checking to see whether the receiveAmount field exists. Before this change, sometimes the rounding tooltip
            would show up on Step 3 of TEX */
      if ($(receiveAmountElementSelector).val()) {
        clearTimeout(roundingMessageTimeout);
        roundingMessageTimeout = setTimeout(() => {
          receiveAmountMessageManagerInstance
            .show(amountRounded.message, amountRounded.messageLevel, amountRounded.analytics);
        }, 100);
      }
    } else {
      receiveAmountMessageManagerInstance.hide();
    }
  }

  /**
   Update UI with any messages
    @method updateUI
    @param {Object} [data] Object returned from ajax call
    @param {Object} [processedErrors] Formatted errors
    @param {MessageObject} [processedErrors.sendLimitError] sendLimitError message object
    @param {MessageObject} [processedErrors.amountRounded] amountRounded message object
    @param {boolean} [processedErrors.hasError] hasError Are errors present?
    @private
    */
  function updateUI(data, processedErrors) {
    const sendLimitError = processedErrors && processedErrors.sendLimitError;
    const amountRounded = processedErrors && processedErrors.amountRounded;

    removeSpinner();

    // Check for send limit error message
    if (sendLimitError) {
      showSendLimitError(processedErrors.hasError, sendLimitError);
    } else {
      sendAmountErrorManagerInstance.hide();
      // if user entered a receive amount, we want to check for rounding error
      if (state.receiveAmountEntered) {
        showAmountRoundedMessage(amountRounded);
      }
    }

    if (isValidAmount) {
      if (opts.isValidAmountCallback) {
        opts.isValidAmountCallback(data);
      }
    } else if (opts.isInvalidAmountCallback) {
      opts.isInvalidAmountCallback(data);
    }

    setQuoteProcessingState(false);

    /* Let other javascript functions know that the pending ajax call has completed */
    if (pendingAjaxCallback) {
      pendingAjaxCallback(getQuoteProcessingState());
    }
  }

  /**
   * Check for error messages and assign to variables for use later
   * and return a boolean for whether there are errors
   * @method processErrors
   * @param resultData - ajax response from getData
   * @returns {{sendLimitError: MessageObject, hasError: Boolean, amountRounded: MessageObject}}
   */
  function processErrors(resultData) {
    const howToSendMoreLink = `<a target='_blank' rel='no-ajax' class='send-limit-more' href='${sendLimitsURL}'>${opts.howICanSendMoreLink}</a>. `;
    const learnMoreLink = `<a target='_blank' rel='no-ajax' class='send-limit-more' href='${sendLimitsURL}'>${opts.learnMoreLink}</a>. `;
    const T2SendLimitLink = `<a href='/send-more' target='_blank' rel='no-ajax'>${opts.T2SendLimitLink}</a>`;
    const highAmountLearnMoreLink = `<a target='_blank' rel='no-ajax' class='send-limit-more' href='/send-more'>${opts.learnMoreLink}</a> `;

    let sendLimitError;
    let amountRounded;
    let hasError = false;

    const supportedLimitsErrorList = [
      {
        code: 'underMinimum',
        isError: true,
      },
      {
        code: 'sendLessNow',
        isError: true,
        link: learnMoreLink,
      },
      {
        code: 'sendAmountLater',
        isError: true,
        link: learnMoreLink,
      },
      {
        code: 'sendLimitsError',
        isError: true,
        link: howToSendMoreLink,
      },
      {
        code: 'overMaximum',
        isError: true,
      },
      {
        code: 'overCountryLimit',
        isError: true,
      },
      {
        code: 'sendLimitsT2Error',
        isError: true,
        link: T2SendLimitLink,
      },
      {
        code: 'maxPayoutError',
        isError: true,
      },
      {
        code: 'highAmountT1',
        defaultMessageLevel: 'warn',
        link: highAmountLearnMoreLink,
      },
      {
        code: 'highAmountT2',
        defaultMessageLevel: 'warn',
        link: highAmountLearnMoreLink,
      },
      {
        code: 'highAmountT3',
        defaultMessageLevel: 'warn',
        link: highAmountLearnMoreLink,
      },
      {
        code: 'highAmountT4',
        defaultMessageLevel: 'warn',
        link: highAmountLearnMoreLink,
      },
      {
        code: 'highAmountSL2',
        defaultMessageLevel: 'warn',
        link: highAmountLearnMoreLink,
      },
      {
        code: 'highAmountSL3',
        defaultMessageLevel: 'warn',
        link: highAmountLearnMoreLink,
      },
      {
        code: 'sourceCurrencyMismatch',
        isError: true,
      },
    ];

    const resultMessages = resultData.messages;
    if (resultMessages) {
      // Loop though array of supported messages, stop on first item that is found in resultMessages.
      // This matches the behaviour of the previous implementation
      $.each(supportedLimitsErrorList, (index, supportedMessage) => { // jshint ignore:line
        const supportedMessageCode = supportedMessage.code;
        const { link } = supportedMessage;
        const resultMessage = resultMessages[supportedMessageCode];
        if (!resultMessage) {
          return true; // message not found in resultMessages, go to next message
        }

        sendLimitError = {
          message: link ? (`${resultMessage.message} ${link}`) : resultMessage.message,
          messageLevel: resultMessage.level || supportedMessage.defaultMessageLevel || 'info',
          analytics: {
            type: supportedMessageCode,
            message: resultMessage.message,
          },
        };

        hasError = !!supportedMessage.isError;

        return false; // break out of loop (original code stops after matching one message from supported list)
      });

      // rounding message to user on Receive Amount field
      if (resultMessages.amountRounded) {
        const confirmLink = messages.confirmLinkMessage;
        const roundingMessageGeneral = `${resultMessages.amountRounded.message} ${resultMessages.roundedAmountIs.message}.`;
        const roundingMessageLayout = "<p>{0} <span class='ppvx_link'> {1}</span></p>";

        amountRounded = {
          message: roundingMessageLayout.replaceTokens(roundingMessageGeneral, confirmLink),
          messageLevel: resultMessages.amountRounded.level || 'warn',
          analytics: {
            type: 'amountRounded',
            message: resultMessages.amountRounded.message,
          },
        };
        hasError = true;
      }
    }

    return {
      amountRounded,
      hasError,
      sendLimitError,
    };
  }

  function getFeesInHeaderTemplate() {
    return ''
          + "<th class='ppvx_text--body-strong fees-in-text'>"
          + '  {0} {1}'
          + '</th>';
  }

  function getFeesLinkTemplate() {
    return `${''
          + "<span class='fees-link'>"
          + "<a href='"}${state.localizedContext}/options/amount-to' class='show-fees ppvx_link' data-ppvx_modal-id='fees-sheet' aria-haspopup='dialog'>`
          + '  {0}'
          + '</a>'
          + '</span>';
  }

  function getFeesRowTemplate() {
    return ''
          + '<tr>'
          + "  <td class='ppvx_text--body'>{0}{1}</td>"
          + "  <td class='ppvx_text--body fees-column'>{2}</td>"
          + '</tr>';
  }

  function getFeesCreditCardDisclaimerTemplate() {
    return ''
          + "<div class='ppvx_text--caption credit-card-disclaimer'>"
          + ' {0}'
          + '</div>';
  }

  function updateFeesInHeader(resultData) {
    const feesInTemplate = getFeesInHeaderTemplate();
    const headerHTML = feesInTemplate.replaceTokens(resultData.feesInText, resultData.sourceCountryCurrency);

    $('.fees-in-text').replaceWith(headerHTML);
  }

  function updateFeesTableLink(resultData) {
    const feesLinkTemplate = getFeesLinkTemplate();
    const linkHTML = feesLinkTemplate.replaceTokens(resultData.feesText);

    $('.fees-link').replaceWith(linkHTML);

    $('.show-fees').bind('click', (evt) => {
      evt.preventDefault();
      if (xoom && xoom.analytics && xoom.analytics.vendors && xoom.analytics.vendors.Fpti) {
        const data = {
          event_name: 'xoom_amount_screen_fees_info_pressed',
          promo: !!resultData.feeInfoText,
        };
        xoom.analytics.vendors.Fpti.trackClick(data);
      }
    });
  }

  function updateFeesTable(resultData) {
    const feesTableTemplate = getFeesRowTemplate();
    const { transactionFees } = resultData;
    let feesTableHTML = "<tbody class='fee-table-rows'>";
    let creditCardDisclaimerFootNoteSymbol = '';
    const creditCardDisclaimerText = resultData.creditCardDisclaimer;
    const creditCardDisclaimerContainer = $('.credit-card-disclaimer');

    updateFeesTableLink(resultData);
    updateFeesInHeader(resultData);

    if (transactionFees.length > 0) {
      transactionFees.forEach((item) => {
        if (item.type === 'credit_card') {
          creditCardDisclaimerFootNoteSymbol = '*';
        } else {
          creditCardDisclaimerFootNoteSymbol = '';
        }
        feesTableHTML += feesTableTemplate.replaceTokens(item.label, creditCardDisclaimerFootNoteSymbol, item.fee);
      });
    }
    feesTableHTML += '</tbody>';

    $('.fee-table-rows').replaceWith(feesTableHTML);

    if (creditCardDisclaimerText) {
      if (creditCardDisclaimerContainer.length > 0) {
        creditCardDisclaimerContainer.innerHTML = creditCardDisclaimerText;
      } else {
        const creditCardDisclaimerTemplate = getFeesCreditCardDisclaimerTemplate();
        $('.fees-information').before(creditCardDisclaimerTemplate.replaceTokens(creditCardDisclaimerText));
      }
    }
  }

  /**
   Process the data returned from the ajax call
    @method processData
    @param {Object} data The response from the ajax call
    @param {Function} callback
    @private
    */
  function processData(data, callback) {
    if (data) {
      result = data;

      const hasCurrencyMismatchError = result.messages && result.messages.sourceCurrencyMismatch;
      // Update the Send Amount and Receive Amount fields with values, avoid setting this values
      // when we get invalid amounts
      if (!hasCurrencyMismatchError) {
        if (state.receiveAmountEntered) {
          if ($sendAmount.is('input')) {
            $sendAmount.val(result.rawSendAmount);
          }
          $receiveAmountEntered.val(true);
        } else {
          if ($receiveAmount.is('input')) {
            $receiveAmount.val(result.rawReceiveAmount);
          } else {
            $receiveAmount.html(result.rawReceiveAmount);
          }
          $receiveAmountEntered.val(false);
        }
      }

      if (result.orderItemId && ($orderItemId.length > 0)) {
        $orderItemId.val(result.orderItemId);
      }

      const processedErrors = processErrors(result);

      // When the source currency and receive currency are the same,
      // the value entered in one field will be copied over to the other field
      if (result.selectedCurrency === result.sourceCountryCurrency) {
        if (state.receiveAmountEntered) {
          $sendAmount.val($receiveAmount.val());
        } else {
          $receiveAmount.val($sendAmount.val());
        }
      }

      updateFeesTable(result);

      // update receipt data
      if (callback) {
        callback(result);
      }

      // update UI error messages
      updateUI(result, processedErrors);
    }
  }

  /**
   Make ajax call to specified url, and callback will
    finish processing data for page
    @method getData
    @param {String} url The url for the ajax call
    @param {Function} callback
    @private
    */
  function getData(url, callback) {
    let urlWithQuote;
    if (xhr) {
      xhr.abort();
    }
    urlWithQuote = url
      .addParam('amount', state.sraAmount)
      .addParam('type', state.sraType)
      .addParam('currency', state.currency)
      .addParam('sourceCurrencyCode', state.sourceCurrencyCode);

    if (paramsForQuote) {
      urlWithQuote = urlWithQuote
        .addParam('destinationCountryCode', state.countryCode)
        .addParam('destinationCurrencyCode', state.currency)
        .addParam('disbursementType', paramsForQuote.disbursementType)
        .addParam('paymentType', paramsForQuote.paymentType)
        .addParam('recipientId', paramsForQuote.recipientId, false, !paramsForQuote.recipientId)
        .addParam('serviceSlug', paramsForQuote.serviceSlug);
    }

    xhr = $.ajax({
      url: `/ajax${urlWithQuote}`,
      dataType: 'json',
      type: 'GET',
      timeout: ajaxTimeoutExpirationMs,
      success(data) {
        ajaxCache[url] = data;
        processData(data, callback);
      },
      error(xhrError, textStatus, error) {
        console.log(xhrError.statusText);
        console.log(textStatus);
        console.log(error);

        if (textStatus === 'timeout') {
          window.location.href.addParam('quoteTimeout', true);
        } else if (textStatus !== 'abort' && opts.reloadOnAjaxError) {
          window.location.reload();
        }
      },
    });
  }

  function validationCallback() {
    if (startValidationCallback) {
      startValidationCallback();
    }
  }

  /**
   Further validation of user-entered data
    @method keyCheck
    @private
    */
  function validateUserData(sendNumber, receiveNumber) {
    validationCallback();

    isValidAmount = 1;

    if (!state.countryCode) {
      console.error('no country code provided');
      return;
    }

    if (!state.currency) {
      console.error('no destination currency provided');
      return;
    }

    if (sendNumber || receiveNumber) {
      setSraType(state.receiveAmountEntered);
      // set quote type and amount for the ajax call
      if (state.receiveAmountEntered) {
        state.sraAmount = receiveNumber.toString();
      } else {
        state.sraAmount = sendNumber.toString();
      }

      getData(state.quoteURL, ajaxCallback);
    }
  }

  /**
   Validate send amount and receive amount data to determine
    whether to process data or not
    @method validate
    @param {Object} validationOptions
    @param {Number} [validationOptions.delay] The amount of delay between entering number and validating it
    @param {Boolean} [validationOptions.useStoredState] Use send/receive amounts stored in state
    @public
    */
  function validate(validationOptions) {
    const localValidationOptions = $.extend({}, {
      delay: 500,
      useStoredState: false,
    }, validationOptions || {});

    let newSendAmountValue = getNumber($(sendAmountElementSelector).val());
    let newReceiveAmountValue = getNumber($(receiveAmountElementSelector).val());

    /* useStoredState is used on MyAccount when we want to manually call validate without using an in-page form */
    if (localValidationOptions.useStoredState) {
      newSendAmountValue = getNumber(state.sendAmount);
      newReceiveAmountValue = getNumber(state.receiveAmount);
    }

    // abort any delayed validate action
    if (validateTimeout) {
      clearTimeout(validateTimeout);
    }

    // abort any pending xhr requests
    if (xhr) {
      xhr.abort();
    }

    // reset isValidAmount flag:  Valid until proven invalid
    isValidAmount = 1;

    // hide the error tooltip
    sendAmountErrorManagerInstance.hide();
    receiveAmountMessageManagerInstance.hide();

    function handleNullValue() {
      $sendAmount.val('');
      $receiveAmount.val('');

      isValidAmount = 0;

      result.error = null; // clear the quote error object, so that we don't show the inline error when we call updateUI

      validationCallback();

      if (updateCurrencyCallback) {
        updateCurrencyCallback(state.currency, state.sourceCurrencyCode);
      }

      if (opts.emptyValueCallback) {
        opts.emptyValueCallback();
      }

      setAmountsCache();

      updateUI(); // trigger updateUI so that the valid/invalid data callbacks fire
    }

    /* Do not make an ajax call if the Receive Amount field is set to nothing by the user,
      or the send amount value is blank */
    if ((!state.receiveAmountEntered && !newSendAmountValue)
          || (state.receiveAmountEntered && !newReceiveAmountValue)) {
      validationCallback();

      handleNullValue();
      removeSpinner();
      // hide the amount field error messages on null value
      sendAmountErrorManagerInstance.hide();
      receiveAmountMessageManagerInstance.hide();
      return;
    }

    // begin validating amount in <delay> seconds
    validateTimeout = setTimeout(() => {
      validateUserData(newSendAmountValue, newReceiveAmountValue);
    }, localValidationOptions.delay);
  }

  /**
   We need to call init in a separate method
    because if we call it anonymously, we cannot unbind this specific binding (we can only unbind all bindings)
    If we place the validate method directly as an argument to the binding, then the validate method will have errors
    because the bind method always passes the event object as the first argument
    @method initValidate
    @private
    */
  function initValidate() {
    setQuoteProcessingState(true);

    /* Let other javascript functions know that this ajax call has begun */
    if (pendingAjaxCallback) {
      pendingAjaxCallback(getQuoteProcessingState());
    }

    validate();
  }

  /**
   * Validate entered data and make ajax call if valid amounts
   * @param {Boolean} receiveAmountWasEntered whether the user entered an amount in the receive field
   */
  function processEnteredAmount(receiveAmountWasEntered) {
    setAmountsCache();

    state.receiveAmountEntered = receiveAmountWasEntered;

    clearTimeout(grayOutField);

    if (receiveAmountWasEntered) {
      showSpinnerInField($sendAmount);
    } else {
      showSpinnerInField($receiveAmount);
    }

    initValidate();
  }

  // The Rounding message manager
  const ReceiveAmountMessageManager = function () {
    let fieldLevelRoundingMsg;

    function hide() {
      $(sendAmountElementSelector).removeClass('error');

      if (fieldLevelRoundingMsg) {
        fieldLevelRoundingMsg.remove();
      }
    }

    function updateRoundedAmt() {
      const receiveAmountWasEntered = true;
      const $receiveAmountField = $(receiveAmountElementSelector);

      // enable continue button
      if (opts.isValidAmountCallback) {
        opts.isValidAmountCallback();
      }

      $receiveAmountField.val(result.rawReceiveAmount);

      // Make ajax call on rounded receive amount
      processEnteredAmount(receiveAmountWasEntered);
    }

    function bindClickToMessage() {
      $('#receive-amount-inline-message').bind('click', () => {
        hide();
        updateRoundedAmt();
      });
    }

    function show(message, level, analytics) {
      // disable continue button
      if (opts.isInvalidAmountCallback) {
        opts.isInvalidAmountCallback();
      }

      const $targetElement = $(messageContainer).length > 0 ? $(messageContainer) : $(receiveAmountElementSelector);

      if ($targetElement.is(':visible')) {
        xoom.UIManager.hide();

        fieldLevelRoundingMsg = xoom.form.InlineErrors.addInlineMessage(
          'receive-amount-inline-message',
          $targetElement,
          message,
          level,
          {
            callback() {
              bindClickToMessage();
              recordMessagePresentedEvents(analytics);
            },
            boundFieldSelector: receiveAmountElementSelector,
            boundFieldClass: amountFieldParentObj || '.xform-row',
            customMsgClasses: inlineErrorCustomClasses,
            msgClass,
          },
        );
      }
    }

    return {
      show,
      hide,
      bindClickToMessage,
      unload() {
        hide();
        xoom.form.Filters.remove(receiveAmountElementId);
      },
    };
  };

  // The send limit message manager
  const SendAmountErrorManager = function () {
    let initError;
    let sendAmountElement;
    let sendAmountElementSelectorError;
    let fieldLevelErrorMsg;

    function setup(selector) {
      if (!initError) {
        if (selector) {
          sendAmountElementSelectorError = selector;
        }
        sendAmountElement = $(sendAmountElementSelectorError);
        initError = 1;
      }
    }

    function hide() {
      if (initError) {
        if (fieldLevelErrorMsg) {
          fieldLevelErrorMsg.remove();
        }

        sendAmountElement.removeClass('error');
      }
    }

    function bindClickToMessage() {
      $('#send-amount-inline-message').bind('click', () => {
        hide();
      });
    }

    function show(message, level, selector, analytics) {
      if (!initError) {
        setup(selector);
      }

      let $targetElement = $('.send-amount');
      const $sendAmountRow = $('#column-send .sendamount-row');

      if (!$targetElement.length || !$targetElement.is(':visible')) {
        if (!$sendAmountRow.length) {
          $targetElement = $(sendAmountElementSelectorError).siblings(':visible').last();
        } else {
          $targetElement = $sendAmountRow;
        }
      }

      xoom.UIManager.hide();

      // insert field level error below field

      fieldLevelErrorMsg = xoom.form.InlineErrors.addInlineMessage(
        'send-amount-inline-message',
        $targetElement,
        message,
        level,
        {
          callback() {
            bindClickToMessage();
            recordMessagePresentedEvents(analytics);
          },
          boundFieldSelector: sendAmountElementSelectorError,
          boundFieldClass: amountFieldParentObj || '.xform-row',
          customMsgClasses: inlineErrorCustomClasses,
          msgClass,
        },
      );

      $targetElement.addClass(level);
    }

    function update(message) {
      if (message) {
        show(message);
      } else {
        hide();
      }
    }

    return {
      bindClickToMessage,
      show,
      hide,
      setup,
      update,
      unload() {
        hide();
        initError = 0;
        xoom.form.Filters.remove(sendAmountElementId);
      },
    };
  };
  /**
   * Add filter to sendAmount and receiveAmount fields
   * to limit the types of data entered into field
   * @method addFilterToAmountFields
   */
  function addFilterToAmountFields() {
    xoom.form.Filters.remove('sendAmount');
    xoom.form.Filters.remove('receiveAmount');

    xoom.form.Filters.add(sendAmountElementId, 'decimal');
    xoom.form.Filters.add(receiveAmountElementId, 'decimal');
  }

  /**
   Set country
    @method setCountry
    @param {String} newCountry country code
    @public
    */
  function setCountry(newCountry) {
    state.countryCode = newCountry;
  }

  /**
   Set currency
    @method setCurrency
    @param {String} newCurrency currency code
    @public
    */
  function setCurrency(newCurrency) {
    state.currency = newCurrency;
  }

  /**
   Set setSourceCurrency
    @method setSourceCurrency
    @param {String} newSourceCurrency currency code
    @public
    */
  function setSourceCurrency(newSourceCurrency) {
    state.sourceCurrencyCode = newSourceCurrency;
  }

  /**
   Set quote url
    @method setQuoteUrl
    @param {String} newUrl
    @public
    */
  function setQuoteUrl(newUrl) {
    state.quoteURL = newUrl;
  }

  function setSendAmount(amount) {
    state.sendAmount = amount;
  }

  function setReceiveAmount(amount) {
    state.receiveAmount = amount;
  }

  function setDisbursementType(newDisbursementType) {
    paramsForQuote.disbursementType = newDisbursementType;
  }

  function setPaymentType(newPaymentType) {
    paramsForQuote.paymentType = newPaymentType;
  }

  function setServiceSlug(newServiceSlug) {
    paramsForQuote.serviceSlug = newServiceSlug;
  }

  /**
   Set setRecipientId
    @method setRecipientId
    @param {String} newRecipientId item id
    @public
    */
  function setRecipientId(newRecipientId) {
    paramsForQuote.recipientId = newRecipientId;
  }

  /**
   * @method updateCurrencyAndExchangeRate
   * @param {Object} formFields
   * @param {Object} $obj
   * @private
   */
  function updateCurrencyAndExchangeRate(formFields, $obj) {
    showSpinnerInField($obj);
    const $exchangeRate = $(formFields.exchangeRateSelector);
    const $amountContainer = $('.amount-container');

    if ($currencySelect.length > 0) {
      state.currency = $currencySelect.find(':selected').val();
    }

    if ($sourceCurrencySelect.length > 0) {
      state.sourceCurrencyCode = $sourceCurrencySelect.find(':selected').val();
    } else {
      state.sourceCurrencyCode = defaultOptions.sourceCurrencyCode;
    }

    if (state.currency === state.sourceCurrencyCode) {
      $exchangeRate.addClass('hide-fx');
      $amountContainer.addClass('hide-fx');
    } else {
      $exchangeRate.removeClass('hide-fx');
      $amountContainer.removeClass('hide-fx');
    }

    if (opts.onCurrencyChangeCallback) {
      opts.onCurrencyChangeCallback();
    }

    validate();
  }

  /**
   * @method fieldValueChanged
   * @param {Object} field DOM field element
   * @param {string} cacheName Name of property in amountsCache
   * @param {boolean} receiveAmountWasEntered
   * @private
   */
  function fieldValueChanged(field, cacheName, receiveAmountWasEntered) {
    if (field.value !== amountsCache[cacheName]) {
      processEnteredAmount(receiveAmountWasEntered);
    }
  }

  /**
   * setOptsValues
   * Set all the variables off of the options passed into component
   * @param options
   */
  function setOptsValues(options) {
    paramsForQuote = options.paramsForQuote;

    if (options.initialCountry) {
      state.countryCode = options.initialCountry;
    } else {
      validate({ delay: 1 }); // call validate to gray out fields and btn
    }

    if (options.selectedCurrency) {
      state.currency = options.selectedCurrency;
    }

    if (options.sourceCurrencyCodeSelected) {
      state.sourceCurrencyCode = options.sourceCurrencyCodeSelected;
    }

    if (options.sendLimitsURL) {
      sendLimitsURL = options.sendLimitsURL;
    }

    if (options.quoteURL) {
      state.quoteURL = options.quoteURL;
    }

    if (options.localizedContext) {
      state.localizedContext = options.localizedContext;
    }

    if (options.messageContainer) {
      messageContainer = options.messageContainer;
    }

    if (options.amountFieldParentObj) {
      amountFieldParentObj = options.amountFieldParentObj;
    }

    if (options.inlineErrorCustomClasses) {
      inlineErrorCustomClasses = options.inlineErrorCustomClasses;
    }

    if (options.msgClass) {
      msgClass = options.msgClass;
    }
  }

  /**
   Unbind the initValidate and message manager from set filters
    @method unload
    @public
    */

  function unload() {
    $(sendAmountElementSelector).unbind('change keyup', initValidate);
    $(receiveAmountElementSelector).unbind('change keyup', initValidate);
    sendAmountErrorManagerInstance.unload();
    receiveAmountMessageManagerInstance.unload();
  }

  /**
   Initialize this module
    @method init
    @param {Object} formFieldsObject Object passing the form field IDs
    @param {String} formFieldsObject.sendAmount ID for send amount input field
    @param {String} formFieldsObject.receiveAmount ID for receive amount input field
    @param {String} formFieldsObject.currencySelector ID for currency select
    @param {String} formFieldsObject.exchangeRateSelector CSS selector for elements that relate
    to the display of the exchange rate
    @param {Object} messagesObject This object contains messages to display
    @param {String} messagesObject.confirmLinkMessage This is the 'OK' in the rounding message
    @param {String} messagesObject.howToSendMoreLink This is the 'How to send more' text after the Send Limits error
    @param {Function} [updateUIFn] Callback function to update receipt
    @param {Function} [startValidationFn] Callback function to start validation
    @param {Function} [currencyUpdateCallback] Callback function to update receipt with Recipient receives currency code
    @param {Object} options
    @param {String} options.destCurrencyCode This is the destination currency
    @param {Boolean} options.receiveAmtEntered Whether user entered a receive amount value or not
    @param {Function} options.isInvalidAmountCallback Function to disable continue button
    @param {Function} options.isValidAmountCallback Function to enable continue button
    @param {String} options.sendLimitsURL This is the URL to open the Sending Limits page
    @param {String} options.quoteURL This is the ajax call URL to get data from the orchestration layer
    @param {String} options.localizedContext This is the base localization part of the url
    @param {Function} options.pendingAjaxCall This function enables or disables the select
    dropdown until the results are returned and UI updated
    @param {String} options.messageContainer This is the message container for the rounding message
    @param {Function} [options.onCurrencyChangeCallback] Callback to execute immediately
    when the currency selection changes
    @param {Object} options.paramsForQuote contains our new options for the RA
    endpoint: disbursementType and paymentType
    @public
    */
  function init(formFieldsObject, messagesObject, updateUIFn, startValidationFn, currencyUpdateCallback, options) {
    // options values
    opts = $.extend({}, defaultOptions, options || {});

    const formFields = $.extend({}, defaultFieldSelectors, formFieldsObject || {});
    $sendAmount = $(formFields.sendAmount);
    $receiveAmount = $(formFields.receiveAmount);
    $currencySelect = $(formFields.currencySelect);
    $sourceCurrencySelect = $(formFields.sourceCurrencySelect);
    $orderItemId = $(formFields.orderItemId);
    amountsCache.sendAmount = $sendAmount.val();
    amountsCache.receiveAmount = $receiveAmount.val();
    $receiveAmountEntered = $(formFields.receiveAmountEntered);
    state.receiveAmountEntered = ($receiveAmountEntered.val() === 'true');

    if ($sendAmount.length === 0) {
      console.error('missing Send Amount field');
      return;
    }

    sendAmountErrorManagerInstance = new SendAmountErrorManager();
    receiveAmountMessageManagerInstance = new ReceiveAmountMessageManager();

    ajaxCallback = updateUIFn;
    startValidationCallback = startValidationFn;
    updateCurrencyCallback = currencyUpdateCallback;

    if (options.pendingAjaxCall) {
      pendingAjaxCallback = options.pendingAjaxCall;
    }

    sendAmountElementSelector = formFields.sendAmount;
    receiveAmountElementSelector = formFields.receiveAmount;
    messages = messagesObject;
    sendAmountElementId = $sendAmount[0].id;
    receiveAmountElementId = $receiveAmount[0].id;

    // bind sendAmount field and turn off autocomplete
    $sendAmount
      .attr('autocomplete', 'off')
      .bind('keyup', function () {
        fieldValueChanged(this, 'sendAmount', false);
      })
      .bind('paste', () => {
        setTimeout(() => { // need timeout so the browser has moment to register the paste into the field
          fieldValueChanged($sendAmount[0], 'sendAmount', false);
        }, 1);
      });

    // bind receiveAmount field and turn off autocomplete
    $receiveAmount
      .attr('autocomplete', 'off')
      .bind('keyup', function () {
        fieldValueChanged(this, 'receiveAmount', true);
      })
      .bind('paste', () => {
        setTimeout(() => { // need timeout so the browser has moment to register the paste into the field
          fieldValueChanged($receiveAmount[0], 'receiveAmount', true);
        }, 1);
      });

    // bind source currency select
    $sourceCurrencySelect.bind('change', (e) => {
      const { id } = e.target;
      $(`label[for=${id}]`).addClass('selected');

      updateCurrencyAndExchangeRate(formFields, $sendAmount);
    });

    // bind currency select
    $currencySelect.bind('change', (e) => {
      const { id } = e.target;
      $(`label[for=${id}]`).addClass('selected');

      state.receiveAmountEntered = false;

      updateCurrencyAndExchangeRate(formFields, $receiveAmount);
    });

    addFilterToAmountFields();

    sendAmountErrorManagerInstance.setup(sendAmountElementSelector);

    setOptsValues(opts);
  }

  /**
   Public Interface for this object
  @object
  @return {Object}
  @public
  */
  xoom.ui.Quote = {
    init,
    getData,
    getQuoteProcessingState,
    processData,
    processEnteredAmount,
    processErrors,
    recordMessagePresentedEvents,
    ReceiveAmountMessageManager,
    removeSpinner,
    SendAmountErrorManager,
    setSendAmount,
    setReceiveAmount,
    setCountry,
    setCurrency,
    setDisbursementType,
    setPaymentType,
    setServiceSlug,
    setReceiveAmtEntered,
    setQuoteUrl,
    setSourceCurrency,
    setRecipientId,
    showSpinnerInField,
    unload,
    updateUI,
    validate,
    validateUserData,
  };

  return xoom;
};

export default initQuote;
